import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateMultiplayerLeague } from '../api/multiplayerApi';
import { useGetLeagueBySlug, useInviteToLeague } from '../api/leaguesApi';
import { useGetTeamPlayers } from '../api/playersApi';
import { SignupGateModal } from '../components/auth/SignupGateModal';
import SquadReveal from '../components/cards/SquadReveal';
import {
    markSquadRevealSeen,
    readSquadRevealSeen,
    shouldShowSquadReveal
} from '../components/cards/squadRevealUtils';
import { useAuth } from '../hooks/useAuth';
import { QuickplayShell } from '../components/quickplay/QuickplayShell';
import { captureEvent } from '../utils/analytics';
import { parseInviteEmails } from '../utils/inviteEmails';
import { getInviteDeliveryFailureMessage, getOwnerJoinabilityErrorMessage } from '../utils/inviteErrors';
import { buildTeamLink } from '../utils/leagueLinks';

// Mirrors the backend capacity in routes/multiplayer.js; leagues always open
// with room for 20 teams, and bye rounds handle any odd team count (one team
// rests per match day).
const MULTIPLAYER_LEAGUE_CAPACITY = 20;

function getEntityId(entity) {
    if (!entity) return null;
    if (typeof entity === 'string') return entity;
    return entity._id ?? entity.id ?? null;
}

function getManagerName(user) {
    return (
        user?.firstName ||
        user?.name?.split?.(' ')?.[0] ||
        user?.username ||
        'My'
    );
}

function MultiplayerBuilder({ user }) {
    const navigate = useNavigate();
    const managerName = getManagerName(user);
    const [leagueName, setLeagueName] = useState(`${managerName}'s League`);
    const [teamName, setTeamName] = useState(`${managerName} FC`);
    const [createdLeague, setCreatedLeague] = useState(null);
    const [copied, setCopied] = useState(false);
    const [inviteEmails, setInviteEmails] = useState('');
    const [inviteError, setInviteError] = useState('');
    const [inviteMessage, setInviteMessage] = useState('');
    const [error, setError] = useState('');
    // Set when the server hands back a friends league the manager already has
    // waiting instead of building a second one.
    const [restoredNotice, setRestoredNotice] = useState('');
    const [createAdditional, setCreateAdditional] = useState(false);
    const [squadRevealRequested, setSquadRevealRequested] = useState(false);
    const [squadRevealDismissed, setSquadRevealDismissed] = useState(false);
    const copiedTimerRef = useRef(null);
    const createLeague = useCreateMultiplayerLeague();
    const inviteToLeague = useInviteToLeague();
    const { data: playersResponse } = useGetTeamPlayers(createdLeague?.teamId);
    const {
        data: leagueResponse,
        isLoading: leagueLoading,
        refetch: refetchLeague
    } = useGetLeagueBySlug(createdLeague?.leagueSlug);

    useEffect(() => {
        if (!createdLeague?.leagueSlug) return undefined;

        const pollId = window.setInterval(() => {
            refetchLeague();
        }, 5000);

        return () => window.clearInterval(pollId);
    }, [createdLeague?.leagueSlug, refetchLeague]);

    useEffect(() => () => {
        if (copiedTimerRef.current) {
            window.clearTimeout(copiedTimerRef.current);
        }
    }, []);

    const league = useMemo(() => {
        const rawLeague = leagueResponse?.data;
        return rawLeague?.league ?? rawLeague ?? null;
    }, [leagueResponse]);

    const teams = Array.isArray(league?.teams)
        ? league.teams
        : createdLeague?.teamId
            ? [{ _id: createdLeague.teamId, name: createdLeague.teamName || teamName, owner: user }]
            : [];
    const ownerPlayers = playersResponse?.data || [];
    // A restored league carries the name it was created with, which is not the
    // default still sitting in the form field, so prefer the saved team.
    const ownerTeamName = teams.find(
        (team) => String(getEntityId(team)) === String(createdLeague?.teamId)
    )?.name || createdLeague?.teamName || teamName;
    // The localStorage seen flag is checked when a share action requests the
    // reveal, not on every render: SquadReveal writes that flag itself at its
    // final stage, so a render-time read would unmount the overlay mid-view
    // on the next poll or copied-state re-render.
    const showSquadReveal = squadRevealRequested && shouldShowSquadReveal({
        gameMode: 'multiplayer',
        isOwnTeam: true,
        players: ownerPlayers,
        seen: squadRevealDismissed
    });

    const requestSquadReveal = () => {
        if (!readSquadRevealSeen(createdLeague.teamId)) {
            setSquadRevealRequested(true);
        }
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        setError('');
        const nextLeagueName = leagueName.trim();
        const nextTeamName = teamName.trim();

        if (!nextLeagueName || !nextTeamName) {
            setError('Please enter both a league name and a team name.');
            return;
        }

        try {
            const result = await createLeague.mutateAsync({
                leagueName: nextLeagueName,
                teamName: nextTeamName,
                ...(createAdditional ? { createAdditional: true } : {})
            });

            if (!result?.leagueId || !result?.leagueSlug || !result?.teamId || !result?.inviteUrl) {
                throw new Error('Nova created the league but did not return all invite details.');
            }

            setCreatedLeague(result);
            setCreateAdditional(false);
            captureEvent('multiplayer_league_created', {
                league_id: result.leagueId,
                amount_of_teams: MULTIPLAYER_LEAGUE_CAPACITY
            });
        } catch (createError) {
            // Coming back to this page (browser back, or the menu link) shows a
            // blank form again, so the server returns the league already
            // waiting instead of quietly building a second one. Reopen it.
            const pendingLeague = createError?.response?.data?.code === 'MULTIPLAYER_LEAGUE_PENDING'
                ? createError.response.data.league
                : null;

            if (pendingLeague?.leagueId && pendingLeague?.leagueSlug
                && pendingLeague?.teamId && pendingLeague?.inviteUrl) {
                setCreatedLeague(pendingLeague);
                // Name the league so the owner knows which one came back. The
                // name is optional on purpose: requiring it would trap an
                // owner whose league lacks one, because retrying the create
                // always returns this same 409.
                setRestoredNotice(pendingLeague.leagueName
                    ? `You already had "${pendingLeague.leagueName}" waiting to kick off, so here it is.`
                    : 'You already had a league waiting to kick off, so here it is.');
                return;
            }

            setError(
                createError?.response?.data?.error ||
                createError?.message ||
                'Nova could not create your league. Please try again.'
            );
        }
    };

    const handleCopyInvite = async () => {
        setError('');

        try {
            if (!navigator.clipboard?.writeText) {
                throw new Error('Copy is not available in this browser. Select and copy the link below.');
            }
            await navigator.clipboard.writeText(createdLeague.inviteUrl);
            requestSquadReveal();
            setCopied(true);
            captureEvent('invite_link_copied', {
                league_id: createdLeague.leagueId
            });
            if (copiedTimerRef.current) {
                window.clearTimeout(copiedTimerRef.current);
            }
            copiedTimerRef.current = window.setTimeout(() => {
                setCopied(false);
                copiedTimerRef.current = null;
            }, 2500);
        } catch (copyError) {
            setError(copyError?.message || 'Nova could not copy the invite link.');
        }
    };

    const handleInviteEmailsChange = (event) => {
        setInviteEmails(event.target.value);
        setInviteError('');
        setInviteMessage('');
    };

    const handleSendInvites = async (event) => {
        event.preventDefault();
        setInviteError('');
        setInviteMessage('');

        const emails = parseInviteEmails(inviteEmails);
        if (!emails) {
            setInviteError('Please enter valid email addresses.');
            return;
        }

        try {
            await inviteToLeague.mutateAsync({
                leagueId: createdLeague.leagueId,
                emails,
                slug: createdLeague.leagueSlug
            });
            requestSquadReveal();
            setInviteEmails('');
            setInviteMessage(`Invites sent to ${emails.length} email(s).`);
        } catch (sendError) {
            // A 409 with a joinabilityReason means nobody new can join; the
            // code is the contract, so show our own wording for it.
            const joinabilityMessage = getOwnerJoinabilityErrorMessage(sendError);
            if (joinabilityMessage) {
                setInviteError(joinabilityMessage);
            } else if (sendError?.response?.status === 409) {
                setInviteError('Some users are already members or have pending invites.');
            } else if (sendError?.response?.status === 429) {
                setInviteError('Too many invites sent. Please try again later.');
            } else {
                setInviteError(
                    getInviteDeliveryFailureMessage(sendError) ||
                    sendError?.response?.data?.error ||
                    sendError?.message ||
                    'Nova could not send the invites. Please try again.'
                );
            }
        }
    };

    const handleContinue = () => {
        captureEvent('multiplayer_invite_continue', {
            league_id: createdLeague.leagueId,
            team_count: teams.length
        });
        // The team pitch reads gameMode and team ownership from requests that
        // land after its own data, so pass the reveal hint the same way the
        // invite-accept flow does. Without it a manager who never copied the
        // invite (and so never saw the reveal here) gets the plain pitch first
        // and the reveal a beat later.
        navigate(
            buildTeamLink(createdLeague.teamId, `/${createdLeague.leagueSlug}`),
            { state: { squadReveal: { teamId: createdLeague.teamId, gameMode: 'multiplayer' } } }
        );
    };

    const handleCreateDifferentLeague = () => {
        setRestoredNotice('');
        setCreatedLeague(null);
        setCreateAdditional(true);
        // Nothing from the restored league's invite panel belongs to the next one.
        setCopied(false);
        setInviteEmails('');
        setInviteError('');
        setInviteMessage('');
        setSquadRevealRequested(false);
        setSquadRevealDismissed(false);
    };

    const handleSquadRevealClose = () => {
        markSquadRevealSeen(createdLeague.teamId);
        setSquadRevealDismissed(true);
    };

    if (!createdLeague) {
        return (
            <section className="play-card multiplayer-create-card" aria-labelledby="multiplayer-title">
                <p className="play-kicker">Play with friends</p>
                <h1 id="multiplayer-title">Build your league in one click</h1>
                <p className="play-intro">
                    Every manager gets a complete squad. Share the invite and start the season once a friend joins.
                </p>

                <form className="multiplayer-create-form" onSubmit={handleCreate}>
                    <label htmlFor="multiplayer-league-name">
                        League name
                        <input
                            id="multiplayer-league-name"
                            value={leagueName}
                            maxLength={80}
                            required
                            onChange={(event) => setLeagueName(event.target.value)}
                        />
                    </label>
                    <label htmlFor="multiplayer-team-name">
                        Your team
                        <input
                            id="multiplayer-team-name"
                            value={teamName}
                            maxLength={80}
                            required
                            onChange={(event) => setTeamName(event.target.value)}
                        />
                    </label>
                    <p className="multiplayer-team-note">Up to 20 teams can join your league.</p>

                    {error ? <p className="error-message" role="alert">{error}</p> : null}

                    <button
                        type="submit"
                        className="play-choice-button primary"
                        disabled={createLeague.isPending}
                    >
                        {createLeague.isPending ? 'Creating league…' : 'Create league'}
                    </button>
                </form>
            </section>
        );
    }

    return (
        <>
            {showSquadReveal ? (
                <SquadReveal
                    teamId={createdLeague.teamId}
                    teamName={ownerTeamName}
                    leagueName={league?.name || createdLeague?.leagueName || leagueName}
                    players={ownerPlayers}
                    onClose={handleSquadRevealClose}
                />
            ) : null}

            <section className="play-card multiplayer-invite-card" aria-labelledby="invite-title">
                <p className="play-kicker">League ready</p>
                <h1 id="invite-title">Invite your friends</h1>
                <p className="play-intro">
                    Share the link or send email invites. Their squads are created when they join.
                </p>

                {restoredNotice ? (
                    <p className="multiplayer-restored-notice" role="status">
                        {restoredNotice}
                        {' '}
                        <button
                            type="button"
                            onClick={handleCreateDifferentLeague}
                        >
                            Create a different league instead
                        </button>
                    </p>
                ) : null}

                <div className="invite-link-panel">
                    <input
                        aria-label="League invite link"
                        readOnly
                        value={createdLeague.inviteUrl}
                    />
                    <button
                        type="button"
                        className="play-choice-button primary"
                        onClick={handleCopyInvite}
                    >
                        {copied ? 'Copied!' : 'Copy invite link'}
                    </button>
                </div>

                <form className="email-invite-panel" onSubmit={handleSendInvites}>
                    <label htmlFor="multiplayer-invite-emails">
                        <span>Invite by email</span>
                        <textarea
                            id="multiplayer-invite-emails"
                            aria-describedby="multiplayer-invite-email-help"
                            aria-invalid={Boolean(inviteError)}
                            placeholder="friend@example.com, teammate@example.com"
                            value={inviteEmails}
                            onChange={handleInviteEmailsChange}
                        />
                    </label>
                    <div className="email-invite-footer">
                        <p id="multiplayer-invite-email-help">
                            Separate multiple emails with commas or new lines.
                        </p>
                        <button
                            type="submit"
                            className="play-choice-button primary"
                            disabled={inviteToLeague.isPending || !inviteEmails.trim()}
                        >
                            {inviteToLeague.isPending ? 'Sending invites…' : 'Send invites'}
                        </button>
                    </div>
                    {inviteError ? <p className="error-message" role="alert">{inviteError}</p> : null}
                    {inviteMessage ? <p className="invite-success-message" role="status">{inviteMessage}</p> : null}
                </form>

                <div className="multiplayer-members">
                    <div className="multiplayer-members-heading">
                        <h2>Teams joined</h2>
                        <span>{teams.length}/{league?.amountOfTeams || MULTIPLAYER_LEAGUE_CAPACITY}</span>
                    </div>
                    {leagueLoading && teams.length === 1 ? (
                        <p>Checking for friends…</p>
                    ) : (
                        <ul>
                            {teams.map((team, index) => (
                                <li key={getEntityId(team) || `${team?.name || 'team'}-${index}`}>
                                    <span>{team?.name || `Team ${index + 1}`}</span>
                                    <small>
                                        {team?.owner?.username || team?.owner?.name || (index === 0 ? 'You' : 'Manager joined')}
                                    </small>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="continue-league-block">
                    <button
                        type="button"
                        className="play-choice-button primary"
                        onClick={handleContinue}
                    >
                        Continue
                    </button>
                    <p>
                        {teams.length < 2
                            ? 'Browse your team now. You can invite friends later and begin the season from the league table once someone joins.'
                            : teams.length % 2 === 1
                                ? 'Continue to your team. You can begin the season from the league table when ready. With an odd number of teams, one team rests each match day.'
                                : 'Continue to your team. You can begin the season from the league table when ready.'}
                    </p>
                </div>

                {error ? <p className="error-message" role="alert">{error}</p> : null}
            </section>
        </>
    );
}

function PlayWithFriendsPage() {
    const { user, session, isGuest: authIsGuest, loading } = useAuth();
    const isGuest = authIsGuest === true || user?.isGuest === true;

    return (
        <QuickplayShell>
            <main className="play-onboarding">
                {loading ? (
                    <section className="play-card">
                        <p className="play-intro">Loading your Nova account…</p>
                    </section>
                ) : !session || !user || isGuest ? (
                    <SignupGateModal
                        inline
                        open
                        redirectTo="/play-with-friends"
                        title={isGuest ? 'Save your team before inviting friends' : 'Sign in to create your league'}
                        description={
                            isGuest
                                ? 'Link your guest team to Google or X, then create a league without losing any progress.'
                                : 'Use Google or X for a fast sign-in, then your league and first squad are created together.'
                        }
                    />
                ) : (
                    <MultiplayerBuilder user={user} />
                )}
            </main>
        </QuickplayShell>
    );
}

export { PlayWithFriendsPage };
